/**
 * Agent memory — learned operator preferences (the `agentPrefs`
 * collection), distilled from how proposals were handled.
 *
 * WHY this exists: the confirm-each-write loop produces a free training
 * signal. Dismissing a kind of proposal again and again, or reverting it
 * after an auto-run, is the operator saying "stop proposing this" — and
 * a connected Claude worker should hear that. Each (actionKind, signal)
 * pair is upserted with a running count; `memoryNotes()` turns the pairs
 * that cleared a small threshold into plain-English lines the chat
 * injects into `AgentContext.memory`, which the worker folds into its
 * prompt (see worker/server.mjs `summarizeContext`).
 *
 * WHY this is not a confirm-card write: it records the operator's OWN
 * choices about proposals (exactly like the autonomy streaks in
 * localStorage) — the agent isn't writing CRM data here. It lives in the
 * DB rather than localStorage because the worker context is assembled
 * client-side but the lesson is about the team's data hygiene, and a
 * shared store survives browsers and benefits everyone.
 *
 * Honest accounting: with no worker configured the local parser ignores
 * memory — the notes only change behavior when real Claude is wired in.
 * The chat header pill shows the count either way so nothing is hidden.
 */

import { db, nowIso, uuid } from '../db/store';
import type { AgentPref } from '../db/types';
import type { AgentAction } from './types';

export type MemorySignal = AgentPref['signal'];

/** A pair must be seen this many times before it becomes a note. One
 *  dismissal is noise; two is a pattern worth telling the worker about. */
export const MEMORY_THRESHOLD = 2;

/** Cap on injected notes — context budget, strongest signals first. */
const MAX_NOTES = 6;

/** Human label per action kind, for readable notes. */
const KIND_LABEL: Record<AgentAction['kind'], string> = {
  create_person: 'new contacts',
  set_prospect_stage: 'prospect stage changes',
  set_customer_status: 'customer status changes',
  create_document: 'proposals/contracts',
  move_workflow_stage: 'workflow stage moves',
  create_ticket: 'new tickets',
  set_ticket_status: 'ticket status changes',
  assign_ticket: 'ticket assignments',
  create_task: 'new tasks',
  schedule_follow_up: 'follow-up scheduling',
  log_activity: 'activity logging',
  update_brain: 'Company Brain facts',
  update_record_field: 'inline field edits',
  send_customer_update: 'weekly customer updates',
  send_drafted_email: 'drafted emails',
  send_sequence_step: 'sequence email sends',
  mark_enrollment_replied: 'sequence reply marks',
  publish_content: 'content publishes',
};

/** Distill a (kind, signal, count) into the line the worker will read. */
function distill(kind: string, signal: MemorySignal, count: number): string {
  const label = KIND_LABEL[kind as AgentAction['kind']] ?? kind;
  return signal === 'dismissed'
    ? `The operator has dismissed ${label} proposals ${count}× — only propose ${label} when explicitly asked.`
    : `The operator has reverted executed ${label} ${count}× — be conservative with ${label} and double-check details before proposing.`;
}

/**
 * Record one preference signal. Upserts the (actionKind, signal) record:
 * bumps the count, refreshes the distilled note and `updatedAt`.
 * Failures are swallowed — memory is a nice-to-have and must never break
 * the dismiss/revert interaction it piggybacks on.
 */
export async function recordSignal(
  action: AgentAction,
  signal: MemorySignal,
  operator?: string,
): Promise<void> {
  try {
    const all = await db.list('agentPrefs');
    const existing = all.find(
      (p) => p.actionKind === action.kind && p.signal === signal,
    );
    const ts = nowIso();
    if (existing) {
      const count = existing.count + 1;
      await db.update('agentPrefs', existing.id, {
        count,
        note: distill(action.kind, signal, count),
        operator: operator ?? existing.operator,
        updatedAt: ts,
      });
    } else {
      await db.add('agentPrefs', {
        id: uuid(),
        actionKind: action.kind,
        signal,
        note: distill(action.kind, signal, 1),
        count: 1,
        operator,
        createdAt: ts,
        updatedAt: ts,
      });
    }
  } catch {
    /* memory must never break the interaction that feeds it */
  }
}

/**
 * The note lines to inject into the worker context: pairs at or above
 * MEMORY_THRESHOLD, strongest first, capped. Returns [] on any failure.
 */
export async function memoryNotes(): Promise<string[]> {
  try {
    const all = await db.list('agentPrefs');
    return all
      .filter((p) => p.count >= MEMORY_THRESHOLD)
      .sort((a, b) => b.count - a.count)
      .slice(0, MAX_NOTES)
      .map((p) => p.note);
  } catch {
    return [];
  }
}

/** All raw preference records (for the chat memory popover). */
export async function listPrefs(): Promise<AgentPref[]> {
  return db.list('agentPrefs');
}

export function subscribePrefs(fn: (prefs: AgentPref[]) => void): () => void {
  return db.subscribe('agentPrefs', fn);
}

/** Forget one learned preference (operator-driven, from the popover). */
export async function forgetPref(id: string): Promise<void> {
  await db.remove('agentPrefs', id);
}

/** Forget everything the agent has learned. */
export async function forgetAllPrefs(): Promise<void> {
  const all = await db.list('agentPrefs');
  await Promise.all(all.map((p) => db.remove('agentPrefs', p.id)));
}
